import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ArrowRightLeft, X, AlertTriangle } from "@/lib/icons";
import { getAsesores, createTransfer, getPendingTransferForLead } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function TransferLeadModal({ open, onClose, lead, currentUser, onTransferred, initialToAsesorId = "" }) {
  const [asesores, setAsesores] = useState([]);
  const [toAsesorId, setToAsesorId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMotivo("");
    setToAsesorId(initialToAsesorId || "");
    (async () => {
      const res = await getAsesores();
      const list = Array.isArray(res) ? res : res?.data || [];
      const filtered = list.filter(
        (a) => a.id !== currentUser?.id && a.rol !== "admin",
      );
      setAsesores(filtered);
      if (lead?.id) {
        const pending = await getPendingTransferForLead(lead.id);
        setPendingTransfer(pending);
      }
    })();
  }, [open, lead?.id, currentUser?.id, initialToAsesorId]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!toAsesorId) {
      toast.error("Selecciona la asesora destinataria");
      return;
    }
    setLoading(true);
    const { data, error } = await createTransfer({
      leadId: lead.id,
      fromAsesorId: currentUser.id,
      toAsesorId,
      motivo: motivo.trim() || null,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || "No se pudo crear la transferencia");
      return;
    }
    const destNombre = asesores.find((a) => a.id === toAsesorId)?.nombre || "la asesora";
    toast.success(`Solicitud enviada a ${destNombre}`);
    onTransferred?.(data);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-rose-700 to-pink-700 text-white" style={{ background: "linear-gradient(135deg, #7a1b33, #a3204b)" }}>
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            <h3 className="font-bold text-base">Transferir prospecto</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {pendingTransfer ? (
            <div className="flex gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 dark:text-amber-300">
                  Ya hay una transferencia pendiente
                </p>
                <p className="text-amber-700 dark:text-amber-400 text-xs mt-1">
                  {pendingTransfer.from_asesor?.nombre} → {pendingTransfer.to_asesor?.nombre}.
                  Espera a que se resuelva antes de enviar otra.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-sm">
                <p className="text-slate-500 dark:text-slate-400 text-xs">Prospecto a transferir</p>
                <p className="font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
                  {lead?.nombre_tutor || "Sin nombre"}
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  {lead?.nombre_alumno && <>👶 {lead.nombre_alumno} • </>}
                  {lead?.nivel_educativo || "Sin nivel"}
                  {lead?.grado_especifico && <> • {lead.grado_especifico}</>}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Asignar a <span className="text-rose-500">*</span>
                </Label>
                <select
                  value={toAsesorId}
                  onChange={(e) => setToAsesorId(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition"
                >
                  <option value="">Selecciona una asesora…</option>
                  {asesores.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Motivo / contexto <span className="text-slate-400 text-xs">(opcional)</span>
                </Label>
                <Textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ej: Quiere info de primaria, no le toca a mí…"
                  rows={3}
                  className="resize-none text-sm"
                />
              </div>

              <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/40 rounded-xl p-3 text-xs text-rose-800 dark:text-rose-300">
                Al transferir, se enviará una solicitud. Cuando la asesora la
                <strong> acepte</strong>, ella verá toda la conversación, datos del prospecto y
                mensajes históricos. <strong>El lead no se mueve hasta que acepte.</strong>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !!pendingTransfer || !toAsesorId}
            className="text-white hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #7a1b33, #a3204b)" }}
          >
            {loading ? "Enviando…" : "Enviar solicitud"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
